import { Badge } from "@/components/ui/badge";
import { Layers } from "lucide-react";

const ROLE_STYLE = {
  owner: "bg-amber-50 text-amber-700 border-amber-200",
  admin: "bg-sky-50 text-sky-700 border-sky-200",
  supervisor: "bg-teal-50 text-teal-700 border-teal-200",
  office: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cleaner: "bg-slate-100 text-slate-600 border-slate-200",
};
const ROLE_RANK = { owner: 0, admin: 1, supervisor: 2, office: 3, cleaner: 4 };
const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

const sortUsers = (arr) =>
  [...arr].sort((a, b) => (ROLE_RANK[a.role] ?? 9) - (ROLE_RANK[b.role] ?? 9) || (a.displayName || "").localeCompare(b.displayName || ""));

export const OrgChart = ({ users, onOpen }) => {
  const byId = {};
  users.forEach((u) => { byId[u.id] = u; });
  const childMap = {};
  const roots = [];
  users.forEach((u) => {
    if (u.reportsTo && byId[u.reportsTo] && u.reportsTo !== u.id) {
      (childMap[u.reportsTo] = childMap[u.reportsTo] || []).push(u);
    } else {
      roots.push(u);
    }
  });

  // Iterative DFS -> flat rows (avoids a self-referential recursive component)
  const rows = [];
  const seen = new Set();
  const stack = sortUsers(roots).reverse().map((u) => ({ u, depth: 0 }));
  while (stack.length) {
    const { u, depth } = stack.pop();
    if (seen.has(u.id)) continue;
    seen.add(u.id);
    const kids = sortUsers(childMap[u.id] || []);
    rows.push({ u, depth, count: kids.length });
    for (let i = kids.length - 1; i >= 0; i--) stack.push({ u: kids[i], depth: depth + 1 });
  }

  if (users.length === 0) return <p className="p-8 text-sm text-slate-400" data-testid="org-chart-empty">No staff to chart.</p>;

  return (
    <div className="p-4" data-testid="org-chart">
      <p className="text-xs text-slate-400 mb-2">Tap anyone to open their profile. Set “Reports to” on a profile to change the hierarchy.</p>
      <div className="space-y-0.5">
        {rows.map(({ u, depth, count }) => (
          <div key={u.id} style={{ marginLeft: depth * 24 }} className={depth > 0 ? "border-l border-slate-200 pl-3" : ""}>
            <button
              onClick={() => onOpen(u.id)}
              data-testid={`org-node-${u.id}`}
              className="group flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors text-left w-full"
            >
              <span className="h-8 w-8 rounded-full bg-brand/10 text-brand text-[11px] font-bold flex items-center justify-center shrink-0">
                {initials(u.displayName)}
              </span>
              <span className="text-sm font-medium text-slate-800 group-hover:text-brand">{u.displayName}</span>
              <Badge variant="outline" className={`capitalize ${ROLE_STYLE[u.role] || ""}`}>{u.role}</Badge>
              {u.status !== "active" && <span className="text-[10px] font-semibold text-red-500 uppercase">inactive</span>}
              {count > 0 && (
                <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                  <Layers className="h-3 w-3" />{count} {count === 1 ? "report" : "reports"}
                </span>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
